import DymoLibrary from 'services/DymoLib';
import MiscUtils from 'services/MiscUtils';
import PhysicianUtils from 'services/PhysicianUtils';
import ClinicUtils from 'services/ClinicUtils';
import moment from 'moment-timezone';
import PatientUtils from 'services/PatientUtils';
import CalendarUtils from 'services/CalendarUtils';

class Dymo {
  printLabel(label) {
    DymoLibrary.getPrinters()
      .then((printers) => {
        if (printers.length === 0) {
          MiscUtils.showToastrError(
            'Print Label',
            'Error: No printers were found connected to this computer',
          );
          return;
        }

        let printer = printers.find((printer) => printer.IsConnected[0] === 'True');

        DymoLibrary.print(printer.Name[0], label)
          .then((response) => {
            MiscUtils.showSuccess(
              'Patient Label',
              'Patient label is now printing on ' + printer.Name[0],
            );
          })
          .catch((err) => {
            MiscUtils.showToastrError(
              'Print Label',
              'Error: Could not print with printer' + printer.Name[0],
            );
          });
      })
      .catch((err) => {
        MiscUtils.showToastrError(
          'Print Label',
          'Error: No printers were found connected to this computer',
        );
      });
  }

  previewLabel(label) {
    return label.render;
  }

  printPatientLabel(patient, extraInfo, type = 'normal') {
    let labelType;
    if (type === 'normal') labelType = LABEL_NORMAL;
    else if (type === 'small') labelType = LABEL_SMALL;
    else if (type === 'mini') labelType = LABEL_MINI;
    let label = this.getPatientLabel(patient, extraInfo, labelType);
    return this.printLabel(label);
  }

  printDrugLabel(patient, physician, clinic, drug) {
    let label = this.getDrugLabel(patient, physician, clinic, drug);
    return this.printLabel(label);
  }

  getPatientLabel(patient, extraInfo = '', labelXml = LABEL_NORMAL) {
    let health_card = patient.health_card || {};
    let { first_name, last_name, date_of_birth, gender } = patient;
    let { health_number, version_code } = health_card;

    let label = labelXml.replace('LAST_NAME', last_name);
    label = label.replace('FIRST_NAME', first_name);

    let address = patient.address || {};

    let line2 = address.line2 ? `${address.line2},` : '';

    label = label.replace('LINE_1', address.line1 || '');
    label = label.replace('LINE_2', line2);
    label = label.replace('CITY', address.city || '');
    label = label.replace('STATE', address.state || '');
    label = label.replace('POSTCODE', address.postcode || '');
    label = label.replace('PHONE', patient.phone || '');

    gender = gender ? (gender === 'Male' ? 'M' : 'F') : '';

    label = label.replace('GENDER', gender);
    label = label.replace('EXTRA_INFO', extraInfo);

    let birthdate = date_of_birth ? PatientUtils.getBirthdate(patient, 'MMM DD, YYYY', false) : '';
    label = label.replace('BIRTHDATE', birthdate);

    let ohipNumber = '';

    if (health_number) ohipNumber = health_number;
    if (version_code) ohipNumber += version_code;

    label = label.replace('OHIP', ohipNumber);
    label = label.replace(
      'CURRENT_DATE',
      CalendarUtils.format(moment().unix(), 'YYYY-MM-DD hh:mmA'),
    );

    return label.trim();
  }

  getDrugLabel(patient, physician = {}, clinic, drug) {
    let labelXml = LABEL_DRUG;
    //patient
    let { first_name, last_name } = patient ?? {};

    let label = labelXml.replace('_LAST_NAME_', last_name);
    label = label.replace('_FIRST_NAME_', first_name);

    //physician
    let physicianInfo = `${PhysicianUtils.getName(physician)}, ${physician.title || ''}, ${
      physician.college_registration || ''
    }`;
    let physicianPhone = ClinicUtils.getPhone(clinic);
    let physicianAddress = ClinicUtils.getAddress(clinic);

    label = label.replace('_NP_INFO_', physicianInfo || '');
    label = label.replace('_NP_PHONE_', physicianPhone || '');
    label = label.replace('_NP_ADDRESS_', physicianAddress || '');

    //drug
    let { id, name, dosage, duration, quantity, expiry, route, frequency, comments } = drug;

    duration = duration ? `For: ${duration}` : '';
    quantity = quantity ? `Qty: ${quantity}` : '';
    expiry = expiry ? `Expiry: ${expiry}` : '';

    label = label.replace('_MED_ID_', id || '');

    label = label.replace('_MED_', name || '');
    label = label.replace('_DOSAGE_', dosage || '');
    label = label.replace('_ROUTE_', route || '');
    label = label.replace('_FREQ_', frequency || '');

    label = label.replace('_DURATION_', duration || '');
    label = label.replace('_QTY_', quantity || '');
    label = label.replace('_EXPIRY_', expiry || '');
    label = label.replace('_COMMENTS_', comments || '');

    label = label.replace('_DATE_', moment().format('YYYY/MM/DD'));

    label = label.split('&').join('&amp;');

    return label.trim();
  }
}

const LABEL_NORMAL = `<?xml version="1.0" encoding="utf-8"?>
<DieCutLabel Version="8.0" Units="twips" MediaType="Default">
  <PaperOrientation>Landscape</PaperOrientation>
  <Id>Address</Id>
  <PaperName>30252 Address</PaperName>
  <DrawCommands>
    <RoundRectangle X="0" Y="0" Width="1581" Height="5040" Rx="270" Ry="270"/>
  </DrawCommands>
  <ObjectInfo>
    <TextObject>
      <Name>TEXT</Name>
      <ForeColor Alpha="255" Red="0" Green="0" Blue="0"/>
      <BackColor Alpha="0" Red="255" Green="255" Blue="255"/>
      <LinkedObjectName></LinkedObjectName>
      <Rotation>Rotation0</Rotation>
      <IsMirrored>False</IsMirrored>
      <IsVariable>False</IsVariable>
      <HorizontalAlignment>Left</HorizontalAlignment>
      <VerticalAlignment>Top</VerticalAlignment>
      <TextFitMode>None</TextFitMode>
      <UseFullFontHeight>True</UseFullFontHeight>
      <Verticalized>False</Verticalized>
      <StyledText>
        <Element>
          <String>LAST_NAME, FIRST_NAME</String>
          <Attributes>
            <Font Family="Helvetica" Size="11" Bold="True" Italic="False" Underline="False" Strikeout="False"/>
            <ForeColor Alpha="255" Red="0" Green="0" Blue="0"/>
          </Attributes>
        </Element>
        <Element>
          <String>
LINE_1
LINE_2 STATE POSTCODE
PHONE
</String>
          <Attributes>
            <Font Family="Helvetica" Size="11" Bold="False" Italic="False" Underline="False" Strikeout="False"/>
            <ForeColor Alpha="255" Red="0" Green="0" Blue="0"/>
          </Attributes>
        </Element>
      </StyledText>
    </TextObject>
    <Bounds X="331.2" Y="57.59995" Width="4442.4" Height="1435.2"/>
  </ObjectInfo>
  <ObjectInfo>
    <TextObject>
      <Name>TEXT_1</Name>
      <ForeColor Alpha="255" Red="0" Green="0" Blue="0"/>
      <BackColor Alpha="0" Red="255" Green="255" Blue="255"/>
      <LinkedObjectName></LinkedObjectName>
      <Rotation>Rotation0</Rotation>
      <IsMirrored>False</IsMirrored>
      <IsVariable>False</IsVariable>
      <HorizontalAlignment>Right</HorizontalAlignment>
      <VerticalAlignment>Middle</VerticalAlignment>
      <TextFitMode>None</TextFitMode>
      <UseFullFontHeight>True</UseFullFontHeight>
      <Verticalized>False</Verticalized>
      <StyledText>
        <Element>
          <String>EXTRA_INFO

GENDER BIRTHDATE
OHIP
</String>
          <Attributes>
            <Font Family="Helvetica" Size="11" Bold="False" Italic="False" Underline="False" Strikeout="False"/>
            <ForeColor Alpha="255" Red="0" Green="0" Blue="0"/>
          </Attributes>
        </Element>
      </StyledText>
    </TextObject>
    <Bounds X="2693.6" Y="57.59995" Width="2260" Height="1435.2"/>
  </ObjectInfo>
</DieCutLabel>`;
const LABEL_SMALL = `<?xml version="1.0" encoding="utf-8"?>
<DieCutLabel Version="8.0" Units="twips" MediaType="Default">
  <PaperOrientation>Landscape</PaperOrientation>
  <Id>Address</Id>
  <PaperName>30252 Address</PaperName>
  <DrawCommands>
    <RoundRectangle X="0" Y="0" Width="1581" Height="5040" Rx="270" Ry="270"/>
  </DrawCommands>
  <ObjectInfo>
    <TextObject>
      <Name>TEXT</Name>
      <ForeColor Alpha="255" Red="0" Green="0" Blue="0"/>
      <BackColor Alpha="0" Red="255" Green="255" Blue="255"/>
      <LinkedObjectName></LinkedObjectName>
      <Rotation>Rotation0</Rotation>
      <IsMirrored>False</IsMirrored>
      <IsVariable>False</IsVariable>
      <HorizontalAlignment>Left</HorizontalAlignment>
      <VerticalAlignment>Top</VerticalAlignment>
      <TextFitMode>None</TextFitMode>
      <UseFullFontHeight>True</UseFullFontHeight>
      <Verticalized>False</Verticalized>
      <StyledText>
        <Element>
          <String>
LAST_NAME, FIRST_NAME</String>
          <Attributes>
            <Font Family="Helvetica" Size="11" Bold="True" Italic="False" Underline="False" Strikeout="False"/>
            <ForeColor Alpha="255" Red="0" Green="0" Blue="0"/>
          </Attributes>
        </Element>
        <Element>
          <String>
BIRTHDATE
OHIP
CURRENT_DATE
</String>
          <Attributes>
            <Font Family="Helvetica" Size="11" Bold="False" Italic="False" Underline="False" Strikeout="False"/>
            <ForeColor Alpha="255" Red="0" Green="0" Blue="0"/>
          </Attributes>
        </Element>
      </StyledText>
    </TextObject>
    <Bounds X="331.2" Y="57.59995" Width="4442.4" Height="1435.2"/>
  </ObjectInfo>
  <ObjectInfo>
    <TextObject>
      <Name>TEXT_1</Name>
      <ForeColor Alpha="255" Red="0" Green="0" Blue="0"/>
      <BackColor Alpha="0" Red="255" Green="255" Blue="255"/>
      <LinkedObjectName></LinkedObjectName>
      <Rotation>Rotation0</Rotation>
      <IsMirrored>False</IsMirrored>
      <IsVariable>False</IsVariable>
      <HorizontalAlignment>Right</HorizontalAlignment>
      <VerticalAlignment>Top</VerticalAlignment>
      <TextFitMode>None</TextFitMode>
      <UseFullFontHeight>True</UseFullFontHeight>
      <Verticalized>False</Verticalized>
      <StyledText>
        <Element>
          <String>
EXTRA_INFO
</String>
          <Attributes>
            <Font Family="Helvetica" Size="11" Bold="False" Italic="False" Underline="False" Strikeout="False"/>
            <ForeColor Alpha="255" Red="0" Green="0" Blue="0"/>
          </Attributes>
        </Element>
      </StyledText>
    </TextObject>
    <Bounds X="2693.6" Y="57.59995" Width="2260" Height="1435.2"/>
  </ObjectInfo>
</DieCutLabel>`;
const LABEL_MINI = `<?xml version="1.0" encoding="utf-8"?>
<DesktopLabel Version="1">
  <DYMOLabel Version="3">
    <Description>DYMO Label</Description>
    <Orientation>Landscape</Orientation>
    <LabelName>ReturnAddressInt</LabelName>
    <InitialLength>0</InitialLength>
    <BorderStyle>SolidLine</BorderStyle>
    <DYMORect>
      <DYMOPoint>
        <X>0.09</X>
        <Y>0.05666666</Y>
      </DYMOPoint>
      <Size>
        <Width>1.976667</Width>
        <Height>0.9033334</Height>
      </Size>
    </DYMORect>
    <BorderColor>
      <SolidColorBrush>
        <Color A="1" R="0" G="0" B="0"></Color>
      </SolidColorBrush>
    </BorderColor>
    <BorderThickness>1</BorderThickness>
    <Show_Border>False</Show_Border>
    <DynamicLayoutManager>
      <RotationBehavior>ClearObjects</RotationBehavior>
      <LabelObjects>
        <TextObject>
          <Name>ITextObject0</Name>
          <Brushes>
            <BackgroundBrush>
              <SolidColorBrush>
                <Color A="0" R="0" G="0" B="0"></Color>
              </SolidColorBrush>
            </BackgroundBrush>
            <BorderBrush>
              <SolidColorBrush>
                <Color A="1" R="0" G="0" B="0"></Color>
              </SolidColorBrush>
            </BorderBrush>
            <StrokeBrush>
              <SolidColorBrush>
                <Color A="1" R="0" G="0" B="0"></Color>
              </SolidColorBrush>
            </StrokeBrush>
            <FillBrush>
              <SolidColorBrush>
                <Color A="0" R="0" G="0" B="0"></Color>
              </SolidColorBrush>
            </FillBrush>
          </Brushes>
          <Rotation>Rotation0</Rotation>
          <OutlineThickness>1</OutlineThickness>
          <IsOutlined>False</IsOutlined>
          <BorderStyle>SolidLine</BorderStyle>
          <Margin>
            <DYMOThickness Left="0" Top="0" Right="0" Bottom="0" />
          </Margin>
          <HorizontalAlignment>Left</HorizontalAlignment>
          <VerticalAlignment>Middle</VerticalAlignment>
          <FitMode>None</FitMode>
          <IsVertical>False</IsVertical>
          <FormattedText>
            <FitMode>None</FitMode>
            <HorizontalAlignment>Left</HorizontalAlignment>
            <VerticalAlignment>Middle</VerticalAlignment>
            <IsVertical>False</IsVertical>
            <LineTextSpan>
              <TextSpan>
                <Text>LAST_NAME, FIRST_NAME</Text>
                <FontInfo>
                  <FontName>Helvetica</FontName>
                  <FontSize>12</FontSize>
                  <IsBold>True</IsBold>
                  <IsItalic>False</IsItalic>
                  <IsUnderline>False</IsUnderline>
                  <FontBrush>
                    <SolidColorBrush>
                      <Color A="1" R="0" G="0" B="0"></Color>
                    </SolidColorBrush>
                  </FontBrush>
                </FontInfo>
              </TextSpan>
            </LineTextSpan>
          </FormattedText>
          <ObjectLayout>
            <DYMOPoint>
              <X>0.1</X>
              <Y>0.1030973</Y>
            </DYMOPoint>
            <Size>
              <Width>1.966667</Width>
              <Height>0.2023993</Height>
            </Size>
          </ObjectLayout>
        </TextObject>
        <TextObject>
          <Name>ITextObject1</Name>
          <Brushes>
            <BackgroundBrush>
              <SolidColorBrush>
                <Color A="0" R="0" G="0" B="0"></Color>
              </SolidColorBrush>
            </BackgroundBrush>
            <BorderBrush>
              <SolidColorBrush>
                <Color A="1" R="0" G="0" B="0"></Color>
              </SolidColorBrush>
            </BorderBrush>
            <StrokeBrush>
              <SolidColorBrush>
                <Color A="1" R="0" G="0" B="0"></Color>
              </SolidColorBrush>
            </StrokeBrush>
            <FillBrush>
              <SolidColorBrush>
                <Color A="0" R="0" G="0" B="0"></Color>
              </SolidColorBrush>
            </FillBrush>
          </Brushes>
          <Rotation>Rotation0</Rotation>
          <OutlineThickness>1</OutlineThickness>
          <IsOutlined>False</IsOutlined>
          <BorderStyle>SolidLine</BorderStyle>
          <Margin>
            <DYMOThickness Left="0" Top="0" Right="0" Bottom="0" />
          </Margin>
          <HorizontalAlignment>Left</HorizontalAlignment>
          <VerticalAlignment>Middle</VerticalAlignment>
          <FitMode>None</FitMode>
          <IsVertical>False</IsVertical>
          <FormattedText>
            <FitMode>None</FitMode>
            <HorizontalAlignment>Left</HorizontalAlignment>
            <VerticalAlignment>Middle</VerticalAlignment>
            <IsVertical>False</IsVertical>
            <LineTextSpan>
              <TextSpan>
                <Text>BIRTHDATE</Text>
                <FontInfo>
                  <FontName>Helvetica</FontName>
                  <FontSize>12</FontSize>
                  <IsBold>False</IsBold>
                  <IsItalic>False</IsItalic>
                  <IsUnderline>False</IsUnderline>
                  <FontBrush>
                    <SolidColorBrush>
                      <Color A="1" R="0" G="0" B="0"></Color>
                    </SolidColorBrush>
                  </FontBrush>
                </FontInfo>
              </TextSpan>
            </LineTextSpan>
          </FormattedText>
          <ObjectLayout>
            <DYMOPoint>
              <X>0.09857616</X>
              <Y>0.2834031</Y>
            </DYMOPoint>
            <Size>
              <Width>1.968091</Width>
              <Height>0.1960518</Height>
            </Size>
          </ObjectLayout>
        </TextObject>
        <TextObject>
          <Name>ITextObject2</Name>
          <Brushes>
            <BackgroundBrush>
              <SolidColorBrush>
                <Color A="0" R="0" G="0" B="0"></Color>
              </SolidColorBrush>
            </BackgroundBrush>
            <BorderBrush>
              <SolidColorBrush>
                <Color A="1" R="0" G="0" B="0"></Color>
              </SolidColorBrush>
            </BorderBrush>
            <StrokeBrush>
              <SolidColorBrush>
                <Color A="1" R="0" G="0" B="0"></Color>
              </SolidColorBrush>
            </StrokeBrush>
            <FillBrush>
              <SolidColorBrush>
                <Color A="0" R="0" G="0" B="0"></Color>
              </SolidColorBrush>
            </FillBrush>
          </Brushes>
          <Rotation>Rotation0</Rotation>
          <OutlineThickness>1</OutlineThickness>
          <IsOutlined>False</IsOutlined>
          <BorderStyle>SolidLine</BorderStyle>
          <Margin>
            <DYMOThickness Left="0" Top="0" Right="0" Bottom="0" />
          </Margin>
          <HorizontalAlignment>Left</HorizontalAlignment>
          <VerticalAlignment>Middle</VerticalAlignment>
          <FitMode>None</FitMode>
          <IsVertical>False</IsVertical>
          <FormattedText>
            <FitMode>None</FitMode>
            <HorizontalAlignment>Left</HorizontalAlignment>
            <VerticalAlignment>Middle</VerticalAlignment>
            <IsVertical>False</IsVertical>
            <LineTextSpan>
              <TextSpan>
                <Text>OHIP</Text>
                <FontInfo>
                  <FontName>Helvetica</FontName>
                  <FontSize>12</FontSize>
                  <IsBold>False</IsBold>
                  <IsItalic>False</IsItalic>
                  <IsUnderline>False</IsUnderline>
                  <FontBrush>
                    <SolidColorBrush>
                      <Color A="1" R="0" G="0" B="0"></Color>
                    </SolidColorBrush>
                  </FontBrush>
                </FontInfo>
              </TextSpan>
            </LineTextSpan>
          </FormattedText>
          <ObjectLayout>
            <DYMOPoint>
              <X>0.09857615</X>
              <Y>0.4794548</Y>
            </DYMOPoint>
            <Size>
              <Width>1.965243</Width>
              <Height>0.2170364</Height>
            </Size>
          </ObjectLayout>
        </TextObject>
      </LabelObjects>
    </DynamicLayoutManager>
  </DYMOLabel>
  <LabelApplication>Blank</LabelApplication>
  <DataTable>
    <Columns></Columns>
    <Rows></Rows>
  </DataTable>
</DesktopLabel>`;
const LABEL_DRUG = `
<?xml version="1.0" encoding="utf-8"?>
<DieCutLabel Version="8.0" Units="twips" MediaType="Default">
  <PaperOrientation>Landscape</PaperOrientation>
  <Id>Address</Id>
  <PaperName>30252 Address</PaperName>
  <DrawCommands>
    <RoundRectangle X="0" Y="0" Width="1581" Height="5040" Rx="270" Ry="270"/>
  </DrawCommands>
  <ObjectInfo>
    <TextObject>
      <Name>TEXT</Name>
      <ForeColor Alpha="255" Red="0" Green="0" Blue="0"/>
      <BackColor Alpha="0" Red="255" Green="255" Blue="255"/>
      <LinkedObjectName></LinkedObjectName>
      <Rotation>Rotation0</Rotation>
      <IsMirrored>False</IsMirrored>
      <IsVariable>False</IsVariable>
      <HorizontalAlignment>Left</HorizontalAlignment>
      <VerticalAlignment>Middle</VerticalAlignment>
      <TextFitMode>None</TextFitMode>
      <UseFullFontHeight>True</UseFullFontHeight>
      <Verticalized>False</Verticalized>
      <StyledText>
        <Element>
          <String>_MED_ID_
</String>
          <Attributes>
            <Font Family="Helvetica" Size="7" Bold="False" Italic="False" Underline="False" Strikeout="False"/>
            <ForeColor Alpha="255" Red="0" Green="0" Blue="0"/>
          </Attributes>
        </Element>
        <Element>
          <String>_LAST_NAME_, _FIRST_NAME_</String>
          <Attributes>
            <Font Family="Helvetica" Size="10" Bold="True" Italic="False" Underline="False" Strikeout="False"/>
            <ForeColor Alpha="255" Red="0" Green="0" Blue="0"/>
          </Attributes>
        </Element>
        <Element>
          <String>
_MED_ _DOSAGE_ _ROUTE_ _FREQ_
_DURATION_ _QTY_ _EXPIRY_
_NP_INFO_
_NP_PHONE_
_NP_ADDRESS_
_COMMENTS_</String>
          <Attributes>
            <Font Family="Helvetica" Size="7" Bold="False" Italic="False" Underline="False" Strikeout="False"/>
            <ForeColor Alpha="255" Red="0" Green="0" Blue="0"/>
          </Attributes>
        </Element>
      </StyledText>
    </TextObject>
    <Bounds X="331.2" Y="57.59995" Width="4442.4" Height="1435.2"/>
  </ObjectInfo>
  <ObjectInfo>
    <TextObject>
      <Name>TEXT_1</Name>
      <ForeColor Alpha="255" Red="0" Green="0" Blue="0"/>
      <BackColor Alpha="0" Red="255" Green="255" Blue="255"/>
      <LinkedObjectName></LinkedObjectName>
      <Rotation>Rotation0</Rotation>
      <IsMirrored>False</IsMirrored>
      <IsVariable>False</IsVariable>
      <HorizontalAlignment>Right</HorizontalAlignment>
      <VerticalAlignment>Middle</VerticalAlignment>
      <TextFitMode>ShrinkToFit</TextFitMode>
      <UseFullFontHeight>True</UseFullFontHeight>
      <Verticalized>False</Verticalized>
      <StyledText>
        <Element>
          <String>_DATE_</String>
          <Attributes>
            <Font Family="Helvetica" Size="7" Bold="False" Italic="False" Underline="False" Strikeout="False"/>
            <ForeColor Alpha="255" Red="0" Green="0" Blue="0"/>
          </Attributes>
        </Element>
      </StyledText>
    </TextObject>
    <Bounds X="4066.343" Y="100.354" Width="805.1326" Height="281.7699"/>
  </ObjectInfo>
</DieCutLabel>`;

export default new Dymo();
